import { FC } from 'react';

import { CustomAlert, CustomButton, Text } from '../../../shared/uiComponents';
import { ORANGE } from '../../../shared/Helpers/colors';
import { InfoFilled } from '../../../shared/Assets/svgIcons';
import { DuplicateMatchProperties } from '../../../redux/API/ClientAPIHelpers/clientProperties';

interface DuplicateWarningModalProperties {
	duplicates: DuplicateMatchProperties[];
	onCancel: () => void;
	onConfirm: () => void;
	open: boolean;
}

interface GroupedDuplicateProperties {
	clientId: string;
	clientName: string;
	matches: Array<{ field: string; value: string }>;
}

const groupDuplicatesByClient = (duplicates: DuplicateMatchProperties[]): GroupedDuplicateProperties[] => {
	const grouped = new Map<string, GroupedDuplicateProperties>();

	duplicates.forEach((duplicate) => {
		if (!grouped.has(duplicate.clientId)) {
			grouped.set(duplicate.clientId, {
				clientId: duplicate.clientId,
				clientName: duplicate.clientName,
				matches: [],
			});
		}
		grouped.get(duplicate.clientId)?.matches.push({
			field: duplicate.matchField,
			value: duplicate.matchValue,
		});
	});

	return Array.from(grouped.values());
};

const getWarningMessage = (field: string, value: string): string => {
	switch (field) {
		case 'email':
			return `This email already exists: ${value}`;
		case 'phone':
			return `This phone number already exists: ${value}`;
		case 'name':
			return `A client with this name already exists: ${value}`;
		default:
			return `Match found: ${value}`;
	}
};

const DuplicateWarningModal: FC<DuplicateWarningModalProperties> = ({ open, duplicates, onConfirm, onCancel }) => {
	const groupedDuplicates = groupDuplicatesByClient(duplicates);

	return (
		<CustomAlert
			open={open}
			onClose={onCancel}
			title="Potential Duplicate Client Found"
			iconBackgroundColor={ORANGE.TERTIARY}
			Icon={({ color }) => <InfoFilled color={color ?? ORANGE.BASE} />}
			Content={() => (
				<div>
					<Text
						title="The information you entered matches existing client records. Please review to ensure you're not creating a duplicate."
						type="psm"
						className="marginBottom16"
					/>
					{groupedDuplicates.map((group) => (
						<div
							key={group.clientId}
							style={{
								padding: '12px',
								marginBottom: '8px',
								borderRadius: '8px',
								backgroundColor: ORANGE.QUATERNARY,
							}}
						>
							<Text title={group.clientName} type="psm" fontWeight="semiBold" className="marginBottom8" />
							{group.matches.map((match, index) => (
								<Text
									key={index}
									title={getWarningMessage(match.field, match.value)}
									type="psm"
									color={ORANGE.DARK[16]}
								/>
							))}
						</div>
					))}
					<div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
						<CustomButton title="Go Back and Review" onClick={onCancel} buttonType="secondaryGray" />
						<CustomButton title="Create Anyway" onClick={onConfirm} />
					</div>
				</div>
			)}
		/>
	);
};

export default DuplicateWarningModal;
